import { useEffect, useState, useRef } from "react";
import { api, INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, Download, Sparkles, Lock, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ELEVATED = ["admin", "dept_lead"];

export default function Orders() {
  const { user } = useAuth();
  const canImport = user && ELEVATED.includes(user.role);
  // Manual is_new_customer override is scoped to a single trusted admin (Viren) for now.
  // Kept configurable via env on the backend; the frontend mirrors the default list here.
  const canOverrideNewFlag = (user?.email || "").toLowerCase() === "viren@fuelled.in";
  const [orders, setOrders] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [openManual, setOpenManual] = useState(false);
  const [bixResult, setBixResult] = useState(null);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const fileRef = useRef();
  const bixFileRef = useRef();

  const load = () => api.get("/orders", { params: { limit: 5000 } }).then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get("/affiliates").then((r) => setAffiliates(r.data));
  }, []);

  const toggleNewCustomer = async (order) => {
    const nextValue = !order.is_new_customer;
    const label = nextValue ? "Mark as NEW customer?" : "Mark as NOT a new customer?";
    if (!window.confirm(`${label}\nOrder ${order.order_ref || ""} — ${order.customer_email || "(no email)"}.\nThe flag will be LOCKED so webhooks can't overwrite it.`)) return;
    setBusyOrderId(order.id);
    try {
      await api.post(`/orders/${order.id}/override-new-customer`, { is_new: nextValue });
      toast.success(nextValue ? "Marked as new customer (locked)" : "Marked as not new customer (locked)");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally {
      setBusyOrderId(null);
    }
  };

  const undoOverride = async (order) => {
    if (!window.confirm("Undo the manual override on this order?\nThe flag will be unlocked and reverted to its previous value; webhooks can auto-detect again.")) return;
    setBusyOrderId(order.id);
    try {
      await api.delete(`/orders/${order.id}/override-new-customer`);
      toast.success("Override removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setBusyOrderId(null);
    }
  };

  const deleteOrder = async (order) => {
    const refLabel = order.shopify_number || order.order_ref || "this order";
    if (!window.confirm(`Delete ${refLabel} permanently?\n\nCustomer: ${order.customer_email || "—"}\nAmount: ₹${(order.amount || 0).toLocaleString("en-IN")}\n\nIf this order was flagged NEW, the affiliate's lifetime counter will be decremented and their tier/commission recomputed. This cannot be undone.`)) return;
    setBusyOrderId(order.id);
    try {
      const { data } = await api.delete(`/orders/${order.id}`);
      toast.success(`Deleted ${data.deleted_order_ref || refLabel}${data.was_new_customer ? " · affiliate counter adjusted" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyOrderId(null);
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/orders/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.imported} orders${res.data.errors.length ? ` (${res.data.errors.length} errors)` : ""}`);
      load();
    } catch (err) { toast.error("Import failed"); }
    e.target.value = "";
  };

  const importBixgrow = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const toastId = toast.loading("Importing Bixgrow reports…");
    try {
      const res = await api.post("/orders/import-bixgrow", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 minutes for large imports
      });
      toast.success(`Imported ${res.data.imported} historical orders`, { id: toastId });
      setBixResult(res.data);
      load();
    } catch (err) { toast.error(formatApiError(err), { id: toastId }); }
    e.target.value = "";
  };

  const affName = (id) => affiliates.find((a) => a.id === id)?.name || "—";

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle={`${orders.length} orders`}
        actions={
          <div className="flex flex-wrap gap-2">
            <input type="file" accept=".csv" ref={fileRef} onChange={importCsv} className="hidden" data-testid="csv-file" />
            <input type="file" accept=".csv" ref={bixFileRef} onChange={importBixgrow} className="hidden" data-testid="bixgrow-file" />
            {canImport && (
              <Button variant="outline" onClick={() => bixFileRef.current?.click()} className="rounded-full h-10" data-testid="import-bixgrow-btn">
                <Download className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Import </span>Bixgrow
              </Button>
            )}
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full h-10" data-testid="import-csv-btn">
              <Upload className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Import </span>CSV
            </Button>
            <Dialog open={openManual} onOpenChange={setOpenManual}>
              <DialogTrigger asChild>
                <Button className="rounded-full h-10" data-testid="new-order-btn"><Plus className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Add </span>Order</Button>
              </DialogTrigger>
              <ManualOrderDialog affiliates={affiliates} onCreated={() => { setOpenManual(false); load(); }} />
            </Dialog>
          </div>
        }
      />
      <div className="p-6 md:p-10">
        <div className="rounded-xl bg-secondary/40 p-3 mb-4 text-xs text-muted-foreground">
          Accepts either format:
          <div className="mt-1"><span className="font-mono">order_ref, order_date, customer_email, customer_name, amount, discount_code</span></div>
          <div className="mt-1"><span className="font-semibold">Shopify Export:</span> <span className="font-mono">Name, Email, Total, Discount Code, Created at, Billing Name</span> — export from Shopify Admin → Orders → Export</div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Order</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Date</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Customer</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Affiliate</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Code</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Amount</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Commission</th>
                {canOverrideNewFlag && <th className="text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">New Flag</th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className={`border-t border-border/50 ${o.status === "cancelled" ? "opacity-60" : ""}`}>
                  <td className="px-6 py-3 text-sm font-mono">
                    {o.order_ref}
                    {o.status === "cancelled" && <span data-testid="order-cancelled-badge" className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-semibold uppercase">Cancelled</span>}
                  </td>
                  <td className="px-6 py-3 text-sm text-muted-foreground">{o.order_date?.slice(0, 10)}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className={o.status === "cancelled" ? "line-through" : ""}>{o.customer_email}</span>
                    {o.is_new_customer && o.status !== "cancelled" && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold uppercase">New</span>}
                    {o.is_new_customer_locked && (
                      <span
                        data-testid={`order-locked-badge-${o.id}`}
                        title={o.manual_new_customer_override ? `Manually set by ${o.manual_new_customer_override.set_by_user_name} on ${new Date(o.manual_new_customer_override.set_at).toLocaleDateString("en-IN", {day:"numeric", month:"short", year:"numeric"})}` : "Manually locked"}
                        className="ml-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-semibold uppercase"
                      >
                        <Lock className="h-2.5 w-2.5" /> Locked
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm">
                    {affName(o.affiliate_id)}
                    {o.attribution_source === "tag" && o.attribution_tag && (
                      <span
                        data-testid={`order-tag-badge-${o.id}`}
                        className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide"
                        title={`Attributed via Shopify tag: ${o.attribution_tag}`}
                      >
                        Tag · {o.attribution_tag}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-xs font-mono">{o.discount_code || (o.attribution_source === "tag" ? <span className="text-muted-foreground italic">via link</span> : "—")}</td>
                  <td className={`px-6 py-3 text-sm font-medium text-right ${o.status === "cancelled" ? "line-through" : ""}`}>{INR(o.amount)}</td>
                  <td className="px-6 py-3 text-sm text-right">{o.status === "cancelled" ? "—" : INR(o.commission_amount)}</td>
                  {canOverrideNewFlag && (
                    <td className="px-6 py-3 text-center">
                      {o.status === "cancelled" ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            data-testid={`toggle-new-${o.id}`}
                            disabled={busyOrderId === o.id}
                            onClick={() => toggleNewCustomer(o)}
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest rounded-full px-2.5 py-1 border transition-colors disabled:opacity-40 ${
                              o.is_new_customer
                                ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                                : "border-border hover:bg-secondary"
                            }`}
                            title={o.is_new_customer ? "Click to mark NOT new" : "Click to mark as NEW customer"}
                          >
                            <Sparkles className="h-3 w-3" /> {o.is_new_customer ? "New" : "Set new"}
                          </button>
                          {o.manual_new_customer_override && (
                            <button
                              data-testid={`undo-override-${o.id}`}
                              disabled={busyOrderId === o.id}
                              onClick={() => undoOverride(o)}
                              title="Undo manual override"
                              className="h-6 w-6 rounded-full bg-secondary hover:bg-secondary/70 flex items-center justify-center disabled:opacity-40"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </button>
                          )}
                          <button
                            data-testid={`delete-order-${o.id}`}
                            disabled={busyOrderId === o.id}
                            onClick={() => deleteOrder(o)}
                            title="Delete order (Viren only)"
                            className="h-6 w-6 rounded-full bg-destructive/10 hover:bg-destructive/20 text-destructive flex items-center justify-center disabled:opacity-40"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={canOverrideNewFlag ? 8 : 7} className="px-6 py-12 text-center text-sm text-muted-foreground">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {bixResult && (
        <Dialog open={!!bixResult} onOpenChange={() => setBixResult(null)}>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle>Bixgrow import complete</DialogTitle></DialogHeader>
            <div className="space-y-1 text-sm">
              <div>📦 Imported orders: <span className="font-semibold">{bixResult.imported}</span></div>
              <div>⏭️ Skipped (already existed): <span className="font-semibold">{bixResult.skipped_already_ingested}</span></div>
              <div>👤 New customers registered: <span className="font-semibold">{bixResult.new_customers_registered}</span></div>
              <div>🎯 Affiliates touched: <span className="font-semibold">{bixResult.affiliates_touched}</span></div>
              {bixResult.total_errors > 0 && (
                <div className="text-amber-600 font-semibold mt-2">⚠️ {bixResult.total_errors} row issues</div>
              )}
            </div>
            <DialogFooter><Button onClick={() => setBixResult(null)} className="rounded-full">Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ManualOrderDialog({ affiliates, onCreated }) {
  const [form, setForm] = useState({
    order_ref: `MAN-${Date.now()}`,
    order_date: new Date().toISOString().slice(0, 10),
    customer_email: "", customer_name: "", amount: "", discount_code: "", affiliate_id: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    try {
      await api.post("/orders", { ...form, amount: parseFloat(form.amount), source: "manual" });
      toast.success("Order added");
      onCreated();
    } catch { toast.error("Could not add order"); }
  };
  return (
    <DialogContent className="rounded-3xl">
      <DialogHeader><DialogTitle>Add Order</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Order Ref</Label><Input value={form.order_ref} onChange={set("order_ref")} className="mt-1 rounded-xl" /></div>
        <div><Label>Date</Label><Input type="date" value={form.order_date} onChange={set("order_date")} className="mt-1 rounded-xl" /></div>
        <div><Label>Customer Email</Label><Input data-testid="new-order-email" value={form.customer_email} onChange={set("customer_email")} className="mt-1 rounded-xl" /></div>
        <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={set("customer_name")} className="mt-1 rounded-xl" /></div>
        <div><Label>Amount (post-discount)</Label><Input data-testid="new-order-amount" type="number" value={form.amount} onChange={set("amount")} className="mt-1 rounded-xl" /></div>
        <div><Label>Discount Code</Label><Input data-testid="new-order-code" value={form.discount_code} onChange={set("discount_code")} className="mt-1 rounded-xl" placeholder="e.g. PRIYA42" /></div>
        <div className="col-span-2"><Label>Affiliate (optional if code resolves)</Label>
          <select value={form.affiliate_id} onChange={set("affiliate_id")} className="mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm">
            <option value="">— Auto from code —</option>
            {affiliates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="submit-order" onClick={submit} className="rounded-full">Add</Button>
      </DialogFooter>
    </DialogContent>
  );
}
